import { useApolloClient } from '@apollo/client';
import type { CustomAttributeData } from '@risksmart-app/domain/src/types/common.types';
import type {
  Assessment_Status_Enum,
  UpdateOversightAssessmentMutation,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTRPC } from 'src/utils/trpc';

import { evictField } from '@/utils/graphqlUtils';

/**
 * tRPC input type for updateOversightAssessment. Assessment has no schedule,
 * so no schedule transformation is needed. The legacy action returns
 * affected_rows (NOT an IdOutput) and takes no OriginalTimestamp — no
 * optimistic lock.
 */
type UpdateOversightAssessmentTRPCInput = {
  Id: string;
  Title: string;
  Status: Assessment_Status_Enum;
  OriginatingItemId?: string | null;
  Summary?: string | null;
  ActualCompletionDate?: string | null;
  NextTestDate?: string | null;
  StartDate?: string | null;
  TargetCompletionDate?: string | null;
  CompletedByUser?: string | null;
  Outcome?: number | null;
  CustomAttributeData?: CustomAttributeData | null;
  OwnerUserIds?: string[];
  OwnerGroupIds?: string[];
  ContributorUserIds?: string[];
  ContributorGroupIds?: string[];
  ViewerUserIds?: string[] | null;
  ViewerGroupIds?: string[] | null;
  TagTypeIds?: string[];
  DepartmentTypeIds?: string[];
};

/**
 * Maps the tRPC mutation response to match the GraphQL mutation structure.
 */
const mapTrpcResponseToGraphQL = (trpcData: {
  affected_rows: number;
}): UpdateOversightAssessmentMutation => {
  return {
    updateOversightAssessmentApi: {
      __typename: 'GenericMutationOutput',
      affected_rows: trpcData.affected_rows,
    },
  };
};

export const useUpdateOversightAssessmentTRPC = () => {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const apolloClient = useApolloClient();
  const mutation = useMutation({
    ...trpc.frontend.oversight.update.mutationOptions({
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.oversight.register.queryKey(),
        });
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.oversight.getById.queryKey(),
        });
        // COI-471-style parity: the Apollo twin also evicts the base
        // assessment-result families (their ratings roll up into the
        // assessment) and re-fetches oversight-specific result reads that
        // ARE migrated. `GetOversightResultsByParentIdDocument` and
        // `GetAllOversightAssessmentResultsDocument` have NO tRPC twin yet
        // (RSP-6476 Cluster 2 thinking-point #1), so they are refreshed via
        // the Apollo eviction below rather than a tRPC invalidation.
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey:
              trpc.frontend.oversight.oversightAssessmentCount.queryKey(),
          }),
          queryClient.invalidateQueries({
            queryKey:
              trpc.frontend.assessment.oversightAssessmentResultCount.queryKey(),
          }),
          queryClient.invalidateQueries({
            queryKey:
              trpc.frontend.assessment.oversightAssessmentRiskAssessmentResultsByRiskId.queryKey(),
          }),
          queryClient.invalidateQueries({
            queryKey:
              trpc.frontend.assessment.oversightAssessmentDocumentAssessmentResultsByDocumentId.queryKey(),
          }),
          queryClient.invalidateQueries({
            queryKey:
              trpc.frontend.assessment.oversightAssessmentObligationAssessmentResultsByObligationId.queryKey(),
          }),
        ]);
        // `getOversightAssessments` (the register / selector / linked-item
        // read) and the three assessment-result reads above are NOT migrated —
        // they are still Apollo `useQuery` on a cache-first policy. Mirror the
        // Apollo twin's `update` callback exactly so those readers refresh.
        evictField(apolloClient.cache, 'risk_assessment_result');
        evictField(apolloClient.cache, 'document_assessment_result');
        evictField(apolloClient.cache, 'obligation_assessment_result');
        evictField(apolloClient.cache, 'oversight_assessment');
      },
    }),
  });

  return {
    updateOversightAssessment: async (
      variables: UpdateOversightAssessmentTRPCInput
    ): Promise<UpdateOversightAssessmentMutation> => {
      const result = await mutation.mutateAsync(variables);

      return mapTrpcResponseToGraphQL(result);
    },
    loading: mutation.isPending,
    error: mutation.error,
  };
};
